import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, BarChart3, Flame, Shield, Target, Trophy, type LucideIcon } from 'lucide-react';
import { GuidedEmpty } from '../components/GuidedEmpty';
import { ModuleHero } from '../components/ModuleHero';
import { PublicEventSection } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import { useActiveTournament } from '../state/TournamentContext';
import { buildPublicEventSnapshot, type PublicEventSnapshot, type PublicPlayerLeader } from '../services/liveGameService';

interface LiveEventResponse {
  ok?: boolean;
  snapshot?: PublicEventSnapshot;
}

export function EventStatsPage() {
  const db = useStore((d) => d);
  const { tournament } = useActiveTournament();
  const [remoteSnapshot, setRemoteSnapshot] = useState<PublicEventSnapshot | null>(null);

  const localSnapshot = useMemo(() => buildPublicEventSnapshot(db, tournament.id), [db, tournament.id]);
  const snapshot = remoteSnapshot?.tournament.id === tournament.id ? remoteSnapshot : localSnapshot;

  useEffect(() => {
    if (!tournament.slug) return;
    let cancelled = false;
    let timer: number | null = null;
    const load = async () => {
      try {
        const response = await fetch(`/api/live/events/${encodeURIComponent(tournament.slug)}`);
        const data = (await response.json()) as LiveEventResponse;
        if (!cancelled && data.ok && data.snapshot) setRemoteSnapshot(data.snapshot);
      } catch {
        // Local store snapshot remains the fallback for dev/test.
      } finally {
        const interval = (snapshot?.analytics.totals.liveGames ?? 0) > 0 ? 7000 : 45000;
        if (!cancelled) timer = window.setTimeout(load, interval);
      }
    };
    void load();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [tournament.slug, snapshot?.analytics.totals.liveGames]);

  if (!snapshot) {
    return (
      <GuidedEmpty
        icon={BarChart3}
        title="Stats are not available yet"
        body="Player and team stats appear after an event has teams, games, and scoring data."
      />
    );
  }

  const leaders = snapshot.playerLeaders;
  const hasPlayerStats = leaders.all.some((row) => row.points > 0 || row.shots > 0 || row.penaltyMinutes > 0 || row.shootoutAttempts > 0);

  return (
    <PublicEventSection>
      <ModuleHero
        eyebrow={snapshot.tournament.format === 'season' ? 'Season stats' : 'Event stats'}
        title="Player leaders and team form."
        description="Scoring leaders, team pace, and full event stats from official scorekeeper data."
        icon={BarChart3}
        accent="arc"
        status={{
          label: `${snapshot.analytics.totals.finalGames} final game${snapshot.analytics.totals.finalGames === 1 ? '' : 's'}`,
          tone: snapshot.analytics.totals.finalGames > 0 ? 'ok' : 'info',
        }}
      />

      <section className="public-stats__kpis">
        <StatTile label="Goals" value={snapshot.analytics.totals.totalGoals} detail="Completed games" />
        <StatTile label="Pace" value={snapshot.analytics.totals.finalGames > 0 ? `${formatNumber(snapshot.analytics.totals.averageGoalsPerFinal)}` : '-'} detail="Goals per final" />
        <StatTile label="Tight finals" value={snapshot.analytics.totals.finalGames > 0 ? snapshot.analytics.totals.oneGoalGames : '-'} detail="One-goal games" />
        <StatTile label="PIM" value={snapshot.analytics.totals.totalPenaltyMinutes} detail="Completed games" />
      </section>

      {!hasPlayerStats ? (
        <GuidedEmpty
          icon={Trophy}
          title="No player stats yet"
          body="Goals, assists, shots, and penalty minutes will populate this page as scorekeepers record games."
        />
      ) : (
        <>
          <section className="public-stats__leaders">
            <LeaderCard eventSlug={snapshot.tournament.slug} title="Points" icon={Trophy} rows={leaders.points} stat={(row) => row.points} statLabel="PTS" />
            <LeaderCard eventSlug={snapshot.tournament.slug} title="Goals" icon={Target} rows={leaders.goals} stat={(row) => row.goals} statLabel="G" />
            <LeaderCard eventSlug={snapshot.tournament.slug} title="Assists" icon={Activity} rows={leaders.assists} stat={(row) => row.assists} statLabel="A" />
            <LeaderCard eventSlug={snapshot.tournament.slug} title="Penalty minutes" icon={Shield} rows={leaders.penaltyMinutes} stat={(row) => row.penaltyMinutes} statLabel="PIM" />
            <LeaderCard eventSlug={snapshot.tournament.slug} title="Shootout goals" icon={Target} rows={leaders.all.filter((row) => row.shootoutAttempts > 0).sort((a, b) => b.shootoutGoals - a.shootoutGoals || b.shootoutPct - a.shootoutPct)} stat={(row) => row.shootoutGoals} statLabel="SO" />
          </section>

          <section className="grid grid--2">
            <div className="card">
              <div className="card__head">
                <div>
                  <h3 className="card__title">
                    <Flame size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
                    Team form
                  </h3>
                  <p className="card__sub">Standings pressure, recent results, and scoring profile.</p>
                </div>
              </div>
              {snapshot.analytics.teamPulse.length === 0 ? (
                <GuidedEmpty icon={Flame} title="No team form yet" body="Team form appears once standings are available." />
              ) : (
                <div className="public-stats__team-list">
                  {snapshot.analytics.teamPulse.map((team) => (
                    <div key={team.teamId} className="public-stats__team-row">
                      <span className="public-stats__team-color" style={{ background: team.primaryColor }} />
                      <div>
                        <Link to={`/events/${snapshot.tournament.slug}/teams/${team.teamId}`}><strong>{team.teamName}</strong></Link>
                        <p>{team.trendLabel} - {team.points} pts - {team.goalsFor}-{team.goalsAgainst}</p>
                      </div>
                      <strong className="mono">{signedNumber(team.goalDifferential)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card">
              <div className="card__head">
                <div>
                  <h3 className="card__title">Shot pressure</h3>
                  <p className="card__sub">Only shown when scorekeepers track shots.</p>
                </div>
              </div>
              {leaders.shots.length === 0 ? (
                <GuidedEmpty icon={Target} title="No shot data yet" body="Some leagues choose not to track shots. If they do, leaders appear here." />
              ) : (
                <div className="public-stats__compact-list">
                  {leaders.shots.slice(0, 8).map((row, index) => (
                    <PlayerRankRow eventSlug={snapshot.tournament.slug} key={row.playerId} row={row} rank={index + 1} stat={row.shots} statLabel="SOG" />
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Goalie leaders</h3>
                <p className="card__sub">Save percentage, goals against, and shootout work from recorded goalie data.</p>
              </div>
            </div>
            {snapshot.goalieLeaders.length === 0 ? (
              <GuidedEmpty icon={Shield} title="No goalie data yet" body="Goalie stats appear when rosters identify goalies or scorer-table events include goalies." />
            ) : (
              <div className="table-scroll">
                <table className="table public-stats__table">
                  <thead>
                    <tr>
                      <th>Goalie</th>
                      <th>Team</th>
                      <th>GP</th>
                      <th>W</th>
                      <th>L</th>
                      <th>SV%</th>
                      <th>GAA</th>
                      <th>SO SV%</th>
                      <th>SO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshot.goalieLeaders.map((row) => (
                      <tr key={row.playerId}>
                        <td><Link to={`/events/${snapshot.tournament.slug}/players/${row.playerId}`}><strong>{row.playerName}</strong></Link></td>
                        <td>{row.teamId ? <Link to={`/events/${snapshot.tournament.slug}/teams/${row.teamId}`}>{row.teamName ?? '-'}</Link> : row.teamName ?? '-'}</td>
                        <td>{row.gamesPlayed}</td>
                        <td>{row.wins}</td>
                        <td>{row.losses}</td>
                        <td>{formatPct(row.savePct)}</td>
                        <td>{formatNumber(row.gaa)}</td>
                        <td>{row.shootoutShotsAgainst > 0 ? formatPct(row.shootoutSavePct) : '-'}</td>
                        <td>{row.shutouts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Full player leaderboard</h3>
                <p className="card__sub">{leaders.all.length} rostered player{leaders.all.length === 1 ? '' : 's'} in this event.</p>
              </div>
            </div>
            <div className="table-scroll">
              <table className="table public-stats__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Team</th>
                    <th>GP</th>
                    <th>G</th>
                    <th>A</th>
                    <th>PTS</th>
                    <th>SOG</th>
                    <th>PIM</th>
                    <th>GWG</th>
                    <th>PPG</th>
                    <th>SHG</th>
                    <th>SO</th>
                  </tr>
                </thead>
                <tbody>
                  {leaders.all.map((row, index) => (
                    <tr key={row.playerId}>
                      <td className="mono">{index + 1}</td>
                      <td><Link to={`/events/${snapshot.tournament.slug}/players/${row.playerId}`}><strong>{row.playerName}</strong></Link></td>
                      <td>{row.teamId ? <Link to={`/events/${snapshot.tournament.slug}/teams/${row.teamId}`}>{row.teamName ?? '-'}</Link> : row.teamName ?? '-'}</td>
                      <td>{row.gamesPlayed}</td>
                      <td>{row.goals}</td>
                      <td>{row.assists}</td>
                      <td className="mono">{row.points}</td>
                      <td>{row.shots}</td>
                      <td>{row.penaltyMinutes}</td>
                      <td>{row.gameWinningGoals}</td>
                      <td>{row.powerPlayGoals}</td>
                      <td>{row.shortHandedGoals}</td>
                      <td>{row.shootoutAttempts > 0 ? `${row.shootoutGoals}/${row.shootoutAttempts}` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </PublicEventSection>
  );
}

function StatTile({ label, value, detail }: { label: string; value: string | number; detail: string }) {
  return (
    <div className="public-stats__kpi">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{detail}</p>
    </div>
  );
}

function LeaderCard({
  eventSlug,
  title,
  icon: Icon,
  rows,
  stat,
  statLabel,
}: {
  eventSlug: string;
  title: string;
  icon: LucideIcon;
  rows: PublicPlayerLeader[];
  stat: (row: PublicPlayerLeader) => number;
  statLabel: string;
}) {
  return (
    <div className="card public-stats__leader-card">
      <div className="card__head">
        <h3 className="card__title">
          <Icon size={16} style={{ verticalAlign: -2, marginRight: 6 }} />
          {title}
        </h3>
      </div>
      {rows.length === 0 ? (
        <div className="public-stats__empty-line">No {title.toLowerCase()} logged yet.</div>
      ) : (
        <div className="public-stats__compact-list">
          {rows.slice(0, 5).map((row, index) => (
            <PlayerRankRow eventSlug={eventSlug} key={row.playerId} row={row} rank={index + 1} stat={stat(row)} statLabel={statLabel} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerRankRow({ eventSlug, row, rank, stat, statLabel }: { eventSlug: string; row: PublicPlayerLeader; rank: number; stat: number; statLabel: string }) {
  return (
    <div className="public-stats__rank-row">
      <span className="public-stats__rank">{rank}</span>
      <div>
        <Link to={`/events/${eventSlug}/players/${row.playerId}`}><strong>{row.playerName}</strong></Link>
        <p>
          {row.teamId ? <Link to={`/events/${eventSlug}/teams/${row.teamId}`}>{row.teamName ?? 'Unassigned'}</Link> : row.teamName ?? 'Unassigned'} - {row.goals}G {row.assists}A
        </p>
      </div>
      <div className="public-stats__rank-stat">
        <strong>{stat}</strong>
        <span>{statLabel}</span>
      </div>
    </div>
  );
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value % 1 === 0 ? String(value) : value.toFixed(1);
}

function formatPct(value: number): string {
  if (!Number.isFinite(value)) return '-';
  return value.toFixed(3).replace(/^0/, '');
}

function signedNumber(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}
